import React, { Component } from "react";
import PropTypes from "prop-types";

import { noop, hexToRGBA, getStrokeDasharray, isDefined } from "../../utils";
import { getXValue } from "../../utils/ChartDataUtil";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import ClickableCircle from "../components/ClickableCircle";

class EachLinhaCruzada extends Component {
  constructor(props) {
    super(props);

    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);
    this.handleHover = this.handleHover.bind(this);
    this.isHover = this.isHover.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.renderSVG = this.renderSVG.bind(this);

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleDragStart() {
    const { xValue, yValue } = this.props;
    this.dragStart = {
      xValue,
      yValue,
    };
  }

  handleDrag(moreProps) {
    const { index, onDrag } = this.props;
    const { xValue, yValue } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
    } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const x = xScale(xValue);
    const y = yScale(yValue);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newXValue = getXValue(
      xScale,
      xAccessor,
      [x - dx, yScale.invert(mouseXY[1])],
      fullData,
    );
    const newYValue = yScale.invert(y - dy);

    onDrag(index, {
      xValue: newXValue,
      yValue: newYValue,
    });
  }

  handleDragComplete(...rest) {
    this.dragStart = null;
    this.props.onDragComplete(...rest);
  }

  isHover(moreProps) {
    const { xValue, yValue, tolerance } = this.props;
    const {
      mouseXY,
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    const lineX = xScale(xValue);
    const lineY = yScale(yValue);
    const [mouseX, mouseY] = mouseXY;

    const toleranceValue = tolerance || 7;

    // Check if mouse is near horizontal line OR vertical line
    const nearHorizontal = Math.abs(mouseY - lineY) <= toleranceValue;
    const nearVertical = Math.abs(mouseX - lineX) <= toleranceValue;

    return nearHorizontal || nearVertical;
  }

  drawOnCanvas(ctx, moreProps) {
    const {
      xValue,
      yValue,
      stroke,
      strokeWidth,
      strokeOpacity,
      strokeDasharray,
    } = this.props;
    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    const x = xScale(xValue);
    const y = yScale(yValue);

    const xRange = xScale.range();
    const xStart = xRange[0];
    const xEnd = xRange[xRange.length - 1];

    const yRange = yScale.range();
    const yStart = yRange[yRange.length - 1];
    const yEnd = yRange[0];

    ctx.save();
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = hexToRGBA(stroke, strokeOpacity);

    const dashSegments = getStrokeDasharray(strokeDasharray);
    if (dashSegments !== "none") {
      ctx.setLineDash(dashSegments.split(",").map((d) => Number(d.trim())));
    } else {
      ctx.setLineDash([]);
    }

    // Draw horizontal line
    ctx.beginPath();
    ctx.moveTo(xStart, y);
    ctx.lineTo(xEnd, y);
    ctx.stroke();

    // Draw vertical line
    ctx.beginPath();
    ctx.moveTo(x, yStart);
    ctx.lineTo(x, yEnd);
    ctx.stroke();

    ctx.restore();
  }

  renderSVG(moreProps) {
    const {
      xValue,
      yValue,
      stroke,
      strokeWidth,
      strokeOpacity,
      strokeDasharray,
    } = this.props;
    const { selected, interactive } = this.props;
    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;
    const { hover } = this.state;

    const x = xScale(xValue);
    const y = yScale(yValue);

    const xRange = xScale.range();
    const xStart = xRange[0];
    const xEnd = xRange[xRange.length - 1];

    const yRange = yScale.range();
    const yStart = yRange[yRange.length - 1];
    const yEnd = yRange[0];

    const strokeDash = getStrokeDasharray(strokeDasharray);

    return (
      <g>
        {/* Horizontal line */}
        <line
          x1={xStart}
          y1={y}
          x2={xEnd}
          y2={y}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDash}
        />
        {/* Vertical line */}
        <line
          x1={x}
          y1={yStart}
          x2={x}
          y2={yEnd}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDash}
        />
        {/* Center handle - show when hovering or selected */}
        {interactive !== false && (hover || selected) && (
          <ClickableCircle
            cx={x}
            cy={y}
            r={4}
            fill={stroke}
            stroke={stroke}
            strokeWidth={1}
            opacity={1}
            interactiveCursorClass="react-stockcharts-move-cursor"
          />
        )}
      </g>
    );
  }

  render() {
    const { selected, hoverText, interactive, interactiveCursorClass } =
      this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const { hover } = this.state;
    const cursorClass =
      interactiveCursorClass || "react-stockcharts-move-cursor";

    return (
      <g>
        <GenericChartComponent
          isHover={interactive !== false ? this.isHover : null}
          onHover={interactive !== false ? this.handleHover : null}
          onUnHover={interactive !== false ? this.handleHover : null}
          onDragStart={interactive !== false ? this.handleDragStart : null}
          onDrag={interactive !== false ? this.handleDrag : null}
          onDragComplete={
            interactive !== false ? this.handleDragComplete : null
          }
          svgDraw={this.renderSVG}
          canvasDraw={this.drawOnCanvas}
          canvasToDraw={getMouseCanvas}
          interactiveCursorClass={
            interactive !== false ? cursorClass : undefined
          }
          selected={selected}
          enableDragOnHover={interactive !== false}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
        />
        <HoverTextNearMouse
          show={hoverTextEnabled && hover && interactive !== false}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }
}

EachLinhaCruzada.propTypes = {
  index: PropTypes.number,
  xValue: PropTypes.any.isRequired,
  yValue: PropTypes.any.isRequired,
  stroke: PropTypes.string.isRequired,
  strokeWidth: PropTypes.number.isRequired,
  strokeOpacity: PropTypes.number.isRequired,
  strokeDasharray: PropTypes.string,
  selected: PropTypes.bool,
  hoverText: PropTypes.object.isRequired,
  onDrag: PropTypes.func,
  onDragComplete: PropTypes.func,
  interactive: PropTypes.bool,
  interactiveCursorClass: PropTypes.string,
  tolerance: PropTypes.number,
};

EachLinhaCruzada.defaultProps = {
  onDrag: noop,
  onDragComplete: noop,
  interactive: true,
  selected: false,
  hoverText: {
    enable: false,
  },
  strokeDasharray: "Solid",
  strokeOpacity: 1,
  strokeWidth: 1,
  tolerance: 7,
};

export default EachLinhaCruzada;
